import { format } from 'date-fns';
import { User } from 'src/models/user/_types';
import { Identifier, Timestamp } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';

import { Group } from '../group/_types';
import { normalize } from '../shared/normalizers';

import {
  Assessment,
  AssessmentImportParams,
  AssessmentReminder,
  AssessmentSequence,
  GroupAssessment,
  UserAssessment,
} from './_types';
import {
  normalizeAssessment,
  normalizeAssessmentSequence,
  normalizeAssessmentSequenceList,
  normalizeUserAssessment,
} from './normalizers';

interface AssessmentListItem {
  id: number;
  name: string;
}

export const fetchAssessmentOptions = async (): Promise<
  AssessmentListItem[]
> => {
  const response = await api.get<AssessmentListItem[]>('/api/v2/assessments', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
    },
  });

  return response.data;
};

export const fetchGroupAssessmentsByGroupId = async ({
  groupId,
  search,
  tags,
  sortBy,
  sortDir,
  page,
  perPage,
}: {
  groupId: Group['id'];
  search?: string;
  tags?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  perPage?: number;
}): Promise<{ groupAssessments: GroupAssessment[]; total: number }> => {
  const response = await api.get(`/api/v3/group_assessments`, {
    params: {
      group_id: groupId,
      'q[tags_name_eq_any]': tags,
      'q[assessment_name_cont]': search,
      'q[s]': sortBy && sortDir && `${decamelize(sortBy || '')} ${sortDir}`,
      page,
      per: perPage,
    },
  });

  let normalizedAssessments = response.data?.group_assessment?.map(
    (groupAssessment) => normalize(groupAssessment)
  );

  const assessmentIds = normalizedAssessments.map(
    (assessment) => assessment.assessmentId
  );
  const assessments = assessmentIds.length
    ? await fetchAssessmentByIds({
        assessmentIds,
      })
    : [];

  normalizedAssessments = normalizedAssessments.map((assessment) => {
    const as = assessments.find((a) => a.id === assessment.assessmentId);
    return { ...assessment, name: as?.name || assessment.name };
  });
  return {
    groupAssessments: normalizedAssessments,
    total: response.data?.meta?.total,
  };
};

export const fetchGroupAssessment = async ({
  groupAssessmentId,
}: {
  groupAssessmentId: GroupAssessment['id'];
}): Promise<GroupAssessment> => {
  const response = await api.get(
    `/api/v3/group_assessments/${groupAssessmentId}`
  );
  return normalize(response.data);
};

export const newGroupAssessment = async ({
  data,
}: {
  data: Partial<GroupAssessment>;
}) => {
  const payload = decamelizeKeys(data, { deep: true });
  removeReadOnlyFields(payload, ['tags']);
  const response = await api.post(`/api/v3/group_assessments`, {
    group_assessment: {
      ...payload,
      tag_list: data.tags?.join(','),
    },
  });
  return normalize(response.data);
};

export const updateGroupAssessment = async ({
  groupAssessmentId,
  data,
}: {
  groupAssessmentId: GroupAssessment['id'];
  data: Partial<GroupAssessment>;
}) => {
  const payload = decamelizeKeys(data, { deep: true });
  removeReadOnlyFields(payload, ['tags']);
  const response = await api.put(
    `/api/v3/group_assessments/${groupAssessmentId}`,
    {
      group_assessment: {
        ...payload,
        tag_list: data.tags?.join(','),
      },
    }
  );
  return normalize(response.data);
};

export const deleteGroupAssessment = async ({
  groupAssessmentId,
}: {
  groupAssessmentId: GroupAssessment['id'];
}) => {
  await api.delete(`/api/v3/group_assessments/${groupAssessmentId}`);
};

export const fetchAssessmentByIds = async ({
  assessmentIds,
}: {
  assessmentIds: GroupAssessment['assessmentId'][];
}): Promise<Assessment[]> => {
  const response = await api.get<Assessment[]>(`/api/v2/assessments`, {
    params: {
      with_ids: assessmentIds.join(','),
    },
  });

  return response.data.map(normalizeAssessment);
};

interface FetchAssignedAssessmentsOptions {
  userId: User['id'];
}

export const fetchAssignedAssessments = async ({
  userId,
}: FetchAssignedAssessmentsOptions): Promise<Assessment[]> => {
  const response = await api.get<Assessment[]>('/api/v2/assessments', {
    params: {
      user_id: userId,
      assigned: true,
      fetch_all: true,
    },
  });

  return response.data.map(normalizeAssessment);
};

interface FetchAssignedAssessmentSequencesResponse {
  grouping: AssessmentSequence[];
  meta: {
    total: number;
  };
}

export const fetchAssignedAssessmentSequences = async (
  userId: User['id']
): Promise<AssessmentSequence[]> => {
  const response = await api.get<FetchAssignedAssessmentSequencesResponse>(
    '/api/v3/groupings',
    {
      params: {
        user_id: userId,
        assigned: true,
      },
    }
  );

  // Normalize the response
  return response.data.grouping.map(normalizeAssessmentSequence);
};

export interface FetchUserAssessmentsResponse {
  assessment: UserAssessment[];
  meta: {
    total: number;
  };
}

interface FetchUserAssessmentsOptions {
  userId: User['id'];
  page?: number;
  perPage?: number;
  searchTerm?: string;
}

export const fetchUserAssessments = async ({
  userId,
  page = 1,
  perPage = 10,
  searchTerm = '',
}: FetchUserAssessmentsOptions): Promise<{
  userAssessments: UserAssessment[];
  total: number;
}> => {
  const response = await api.get<FetchUserAssessmentsResponse>(
    '/api/v2/user_assessments',
    {
      params: {
        user_id: userId,
        page,
        per: perPage,
        'q[assessment_name_cont]': searchTerm,
      },
    }
  );

  return {
    userAssessments: response.data.assessment.map(normalizeUserAssessment),
    total: response.data.meta.total,
  };
};

export const deleteUserAssessment = async (
  userAssessmentId: UserAssessment['id']
): Promise<UserAssessment> => {
  const response = await api.delete<UserAssessment>(
    `/api/v2/user_assessments/${userAssessmentId}`
  );

  return normalizeUserAssessment(response.data);
};

interface ExportUserAssessmentResponse {
  resource_id: string;
}

export const exportUserAssessment = async (
  userAssessmentId: UserAssessment['id']
): Promise<string> => {
  const response = await api.get<ExportUserAssessmentResponse>(
    `/api/v2/user_assessments/${userAssessmentId}/export_assessment/`
  );

  return response.data.resource_id;
};

interface AssignAssessmentPayload {
  userId: User['id'];
  itemId: string;
  itemType: 'assessment' | 'sequence';
  sendReminders: false | Timestamp;
  unassignAfterCompletion: boolean;
}

export const assignAssessment = async ({
  userId,
  itemId,
  itemType,
  sendReminders,
  unassignAfterCompletion,
}: AssignAssessmentPayload): Promise<void> => {
  const payload: Record<string, any> = {
    user_id: userId,
    assessment_reminders: [sendReminders, unassignAfterCompletion],
  };

  if (itemType === 'assessment') {
    payload.assigned_assessment = itemId;
  } else if (itemType === 'sequence') {
    payload.assigned_assessment_sequence = itemId;
  } else {
    throw new Error('Invalid item type');
  }

  const response = await api.put(
    '/api/v2/assessments/assign_assessment',
    payload
  );

  return response.data;
};

export interface AssessmentSequenceListItem {
  id: number;
  name: string;
}

export const fetchAssessmentSequenceOptions = async (): Promise<
  AssessmentSequenceListItem[]
> => {
  const response = await api.get<AssessmentSequenceListItem[]>(
    '/api/v3/groupings',
    {
      params: {
        fetch_all: true,
        groupable_type: 'Assessment',
        pluck_columns: 'groupings.id,groupings.name',
        'q[s]': 'name asc',
      },
    }
  );

  return response.data.map(normalizeAssessmentSequenceList);
};

export const fetchAssessmentSequences = async (): Promise<
  AssessmentSequence[]
> => {
  const response = await api.get<AssessmentSequence[]>('/api/v3/groupings', {
    params: {
      fetch_all: true,
      groupable_type: 'Assessment',
      'q[s]': 'name asc',
    },
  });

  return response.data.map(normalizeAssessmentSequence);
};

interface UnassignResponse {
  success: boolean;
  for_group?: boolean;
}

interface UnassignParams {
  userId: User['id'];
  itemId: Identifier;
}

export const unassignAssessment = async ({
  userId,
  itemId,
}: UnassignParams): Promise<UnassignResponse> => {
  const response = await api.get<UnassignResponse>(
    `/api/v2/assessments/${itemId}/remove_assigned/`,
    {
      params: {
        user_id: userId,
        type: 'assessment',
      },
    }
  );

  return response.data;
};

export const unassignAssessmentSequence = async ({
  userId,
  itemId,
}: UnassignParams): Promise<UnassignResponse> => {
  const response = await api.get<UnassignResponse>(
    `/api/v2/assessments/${itemId}/remove_assigned/`,
    {
      params: {
        user_id: userId,
        type: 'grouping',
      },
    }
  );

  return response.data;
};

interface FetchRemindersOptions {
  userId: User['id'];
}

export const fetchAssignmentReminders = async ({
  userId,
}: FetchRemindersOptions): Promise<AssessmentReminder[]> => {
  const response = await api.get('/api/v2/assessments/reminder_assessments', {
    params: { user_id: userId },
  });

  // Normalize the response
  return response.data.map((reminder) => ({
    id: reminder.id,
    type: reminder.type,
    unassignAfterCompletion: reminder.unassign_after_completion,
    reminder: reminder.reminder,
  }));
};

export const importAssessmentSequence = async (
  params: AssessmentImportParams
): Promise<void> => {
  await api.post(
    `/api/v2/assessments/${params.sequenceId}/copy_to_calendar`,
    params
  );
};

export const bulkImportAssessmentSequence = async (
  params: AssessmentImportParams
): Promise<void> => {
  await api.get(
    `/api/v2/assessments/${params.sequenceId}/bulk_copy_to_calendar`,
    {
      params: {
        ...decamelizeKeys({
          ...params,
          startDate: format(new Date(params.startDate), 'MM/dd/yyyy'),
        }),
      },
    }
  );
};
